import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import copy from 'copy-to-clipboard';
import apiService, { PaymentRequest } from '../../services/api';

interface PaymentModalProps {
  payment: PaymentRequest;
  onSuccess: () => void;
  onClose: () => void;
}

type PaymentState = 'pending' | 'confirmed' | 'expired' | 'error';

const PaymentModal: React.FC<PaymentModalProps> = ({ payment, onSuccess, onClose }) => {
  const [status, setStatus] = useState<PaymentState>('pending');
  const [copied, setCopied] = useState<'address' | 'amount' | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Calculate time remaining
  useEffect(() => {
    const updateTime = () => {
      const expires = new Date(payment.expires_at).getTime();
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expires - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0 && status === 'pending') {
        setStatus('expired');
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [payment.expires_at, status]);

  // Poll for payment status
  const checkPaymentStatus = useCallback(async () => {
    if (status !== 'pending') return;

    try {
      const response = await apiService.payments.getStatus(payment.id);
      if (response.status === 'confirmed') {
        setStatus('confirmed');
        // Wait for success animation then call onSuccess
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else if (response.status === 'expired') {
        setStatus('expired');
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  }, [payment.id, status, onSuccess]);

  useEffect(() => {
    if (status !== 'pending') return;

    // Poll every 3 seconds
    const interval = setInterval(checkPaymentStatus, 3000);
    return () => clearInterval(interval);
  }, [checkPaymentStatus, status]);

  // Manual verification
  const handleVerify = async () => {
    try {
      const response = await apiService.payments.verify(payment.id);
      if (response.status === 'confirmed') {
        setStatus('confirmed');
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setError('Payment not found on-chain yet. Please wait...');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleCopy = (text: string, type: 'address' | 'amount') => {
    copy(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">
              {status === 'confirmed' ? 'Payment Confirmed!' :
               status === 'expired' ? 'Payment Expired' :
               'Complete Payment'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Confirmed State */}
          {status === 'confirmed' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex flex-col items-center py-8"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-4"
              >
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
              <h4 className="text-2xl font-bold text-green-400 mb-2">Payment Successful!</h4>
              <p className="text-gray-400 text-center">
                Starter tier activated - lifetime access
              </p>
            </motion.div>
          )}

          {/* Expired State */}
          {status === 'expired' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-red-400 mb-2">Payment Expired</h4>
              <p className="text-gray-400 text-center mb-4">
                This payment request has expired. Please create a new payment.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Pending State */}
          {status === 'pending' && (
            <>
              {/* Timer */}
              <div className={`text-center mb-4 ${timeRemaining < 60 ? 'text-red-400' : 'text-gray-400'}`}>
                <span className="text-sm">Expires in </span>
                <span className="font-mono font-semibold">{formatTime(timeRemaining)}</span>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-white rounded-xl">
                  <QRCodeSVG
                    value={payment.qr_data || payment.payment_url || ''}
                    size={200}
                    level="M"
                    includeMargin={false}
                  />
                </div>
              </div>

              {/* Scan Instructions */}
              <p className="text-center text-gray-400 text-sm mb-6">
                Scan with Phantom, Solflare, or any Solana wallet
              </p>

              {/* Payment Details */}
              <div className="space-y-3 mb-6">
                {/* Amount */}
                <div className="p-3 bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Amount</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{payment.amount_usdc} USDC</span>
                      <button
                        onClick={() => handleCopy(payment.amount_usdc.toString(), 'amount')}
                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                      >
                        {copied === 'amount' ? (
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Receiving Address */}
                <div className="p-3 bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Send to</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">
                        {payment.receiving_wallet.slice(0, 4)}...{payment.receiving_wallet.slice(-4)}
                      </span>
                      <button
                        onClick={() => handleCopy(payment.receiving_wallet, 'address')}
                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                      >
                        {copied === 'address' ? (
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Plan Info */}
                <div className="p-3 bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Plan</span>
                    <span className="font-semibold">{payment.plan.name}</span>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-red-400 text-sm text-center"
                >
                  {error}
                </motion.div>
              )}

              {/* Status Indicator */}
              <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-4">
                <motion.div
                  className="w-2 h-2 bg-yellow-500 rounded-full"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <span>Waiting for payment...</span>
              </div>

              {/* Manual Verify Button */}
              <button
                onClick={handleVerify}
                className="w-full py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors text-sm"
              >
                I've sent the payment
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentModal;
